/**
 * js/api.js — THE single place every backend call lives.
 *
 * UI code should import only this module:  import api from "./js/api.js";
 * then call e.g. `await api.images.list({ sort: "pop" })`.
 *
 * Each method maps 1:1 to an endpoint in config/endpoints.js and goes through
 * common/http.js, which transparently serves mock data (js/*_data.json) or hits
 * the real backend depending on CONFIG.USE_MOCK. Callers never change.
 *
 * ES module. Works today against mocks; works against your backend the moment
 * you implement the routes in docs/API.md and set USE_MOCK=false.
 */
import http from "../common/http.js";
import ENDPOINTS from "../config/endpoints.js";
import CONFIG from "../config/config.js";
import { session } from "../common/storage.js";
import { sleep } from "../common/utils.js";

/* ─────────────────────────────  AUTH  ───────────────────────────── */
const auth = {
  /** Email/password login. Persists token + user, returns the user. */
  async login({ email, password }) {
    const res = await http.post(ENDPOINTS.auth.login(), { email, password });
    if (res.token) session.setToken(res.token);
    if (res.user) session.setUser(res.user);
    return res.user;
  },

  /** Email/password registration. Persists token + user, returns the user. */
  async register({ email, password }) {
    const res = await http.post(ENDPOINTS.auth.register(), { email, password });
    if (res.token) session.setToken(res.token);
    if (res.user) session.setUser(res.user);
    return res.user;
  },

  /** Finish an OAuth login. `payload` is { idToken } (Google) or { code } (Kakao). */
  async loginWithProvider(provider, payload) {
    const res = await http.post(ENDPOINTS.auth.oauth(provider), payload);
    if (res.token) session.setToken(res.token);
    if (res.user) session.setUser(res.user);
    return res.user;
  },

  /** Server logout (best-effort) + clear local session. */
  async logout() {
    try { await http.post(ENDPOINTS.auth.logout()); } catch (e) { /* ignore */ }
    session.clear();
  },

  /** Current user from the session token, or null if unauthenticated. */
  async me() {
    if (!session.isAuthed()) return null;
    const res = await http.get(ENDPOINTS.auth.me());
    if (res && res.user) session.setUser(res.user);
    return res ? res.user : null;
  },

  isAuthenticated() { return session.isAuthed(); },
  cachedUser() { return session.getUser(); },
};

/* ────────────────────────────  IMAGES  ──────────────────────────── */
const images = {
  /** Gallery list. Returns { items, nextCursor, total }. */
  list({ sort = "pop", tag, q, cursor, limit = CONFIG.PAGE_SIZE } = {}) {
    return http.get(ENDPOINTS.images.list(), { query: { sort, tag, q, cursor, limit } });
  },

  /** Single image (full detail). Returns the Image. */
  async get(id) {
    const res = await http.get(ENDPOINTS.images.get(id));
    return res.image || res;
  },

  /** Similar images for the detail page. Returns { items }. */
  similar(id, { limit = 6 } = {}) {
    return http.get(ENDPOINTS.images.similar(id), { query: { limit } });
  },

  /** Like / unlike. Returns { liked, likes }. */
  like(id) { return http.post(ENDPOINTS.images.like(id)); },
  unlike(id) { return http.del(ENDPOINTS.images.like(id)); },

  /** Save (bookmark) / unsave. Returns { saved, saves }. */
  save(id) { return http.post(ENDPOINTS.images.save(id)); },
  unsave(id) { return http.del(ENDPOINTS.images.save(id)); },

  /** Get a download URL. Returns { url, resolution, expiresAt }. */
  download(id, { resolution = "hi" } = {}) {
    return http.get(ENDPOINTS.images.download(id), { query: { resolution } });
  },

  /** Make a fresh set of variations of an image. Returns { items }. */
  variations(id, { count = 4, seed } = {}) {
    return http.post(ENDPOINTS.images.variations(id), { count, seed });
  },

  /** Soft-delete an image (owner, or any image when admin). Returns { id, deleted }. */
  remove(id) { return http.del(ENDPOINTS.images.remove(id)); },
};

/* ─────────────────────────  GENERATIONS  ────────────────────────── */
const generations = {
  /** Kick off a generation job. Returns { id, status, progress }. */
  create(params, { idempotencyKey } = {}) {
    const headers = idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined;
    return http.post(ENDPOINTS.generations.create(), params, { headers });
  },

  /** Poll a job's status. Returns { id, status, progress, results }. */
  get(id) { return http.get(ENDPOINTS.generations.get(id)); },

  /**
   * Convenience: create + poll until done|failed.
   * onProgress(progress 0-100, job) is called on each poll.
   * Returns the finished job (with .results), or throws on failure/timeout.
   */
  async run(params, { onProgress } = {}) {
    const job = await this.create(params);
    if (onProgress) onProgress(job.progress || 0, job);
    const deadline = Date.now() + CONFIG.GENERATION_TIMEOUT_MS;
    let cur = job;
    while (cur.status === "queued" || cur.status === "running") {
      if (Date.now() > deadline) throw new Error("Generation timed out");
      await sleep(CONFIG.GENERATION_POLL_MS);
      cur = await this.get(job.id);
      if (onProgress) onProgress(cur.progress || 0, cur);
    }
    if (cur.status !== "done") throw new Error("Generation failed");
    return cur;
  },
};

/* ───────────────────────────  USERS  ────────────────────────────── */
const users = {
  /** The signed-in user's profile + stats + credits. */
  me() { return http.get(ENDPOINTS.users.me()); },

  /** A public profile by handle. */
  profile(handle) { return http.get(ENDPOINTS.users.profile(handle)); },

  /** My works by tab. type: "created" | "saved" | "liked". Returns { items, nextCursor }. */
  works({ type = "created", cursor, limit = CONFIG.PAGE_SIZE } = {}) {
    return http.get(ENDPOINTS.users.works(), { query: { type, cursor, limit } });
  },

  /** Credit balance. Returns { balance, dailyFree, max }. */
  credits() { return http.get(ENDPOINTS.users.credits()); },

  /** Follow / unfollow a creator. Returns { following, followers }. */
  follow(handle) { return http.post(ENDPOINTS.users.follow(handle)); },
  unfollow(handle) { return http.del(ENDPOINTS.users.follow(handle)); },
};

/* ──────────────────────────  API KEYS  ──────────────────────────── */
const apiKeys = {
  /** Active keys. Returns { items: [{ id, name, keyPrefix, createdAt, lastUsedAt }] }. */
  list() { return http.get(ENDPOINTS.apiKeys.list()); },

  /** Create a key. Returns { id, name, keyPrefix, createdAt, key } — `key` is shown ONCE. */
  create({ name }) { return http.post(ENDPOINTS.apiKeys.create(), { name }); },

  /** Revoke (soft-delete) a key. Returns { id, revoked }. */
  revoke(id) { return http.del(ENDPOINTS.apiKeys.revoke(id)); },
};

/* ──────────────────────────  CATALOG  ───────────────────────────── */
const catalog = {
  tags() { return http.get(ENDPOINTS.catalog.tags()); },
  styles() { return http.get(ENDPOINTS.catalog.styles()); },
  models() { return http.get(ENDPOINTS.catalog.models()); },
  palettes() { return http.get(ENDPOINTS.catalog.palettes()); },
  /** Everything the UI needs to render forms/filters, in one round-trip. */
  all() { return http.get(ENDPOINTS.catalog.all()); },
};

export const api = { auth, images, generations, users, catalog, apiKeys };
export default api;
